/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    NOTIFICATIONS_SPLIT_COMPONENTS
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted toast components from notifications.ts.
 *
 * Change ID:    STYLE_EXTRACTION_TOAST
 * Date:         2026-02-07
 * Modified By:  Claude
 *
 * Description:
 * Extracted inline styles to SCSS module.
 */

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCircleCheck,
  faTriangleExclamation,
  faCircleXmark,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "sonner";
// [2026-02-07] style-extraction: import SCSS module
import styles from "./toastComponents.module.scss";

interface ToastComponentProps {
  title?: string; // [2026-02-06] Made optional - only show if provided
  message: string;
  icon: any;
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
  onClose: () => void;
}

// [2026-02-07] style-extraction: moved inline styles to SCSS module
const BaseToast: React.FC<ToastComponentProps> = ({
  title,
  message,
  icon,
  iconColor,
  backgroundColor,
  borderColor,
  onClose,
}) => {
  // Dynamic colors via CSS custom properties
  const dynamicStyles = {
    "--toast-bg": backgroundColor,
    "--toast-border": borderColor,
    "--toast-icon-color": iconColor,
  } as React.CSSProperties;

  return (
    <div className={`toast-figma-base ${styles.toastBase}`} style={dynamicStyles}>
      <div className={styles.toastIcon}>
        <FontAwesomeIcon icon={icon} className={styles.toastIconSvg} />
      </div>
      <div className={styles.toastContent}>
        {/* [2026-02-06] Only render title if provided */}
        {title && (
          <div className={`u-P1-18-semi-bold ${styles.toastTitle}`}>{title}</div>
        )}
        <div className={`u-P2-16 ${styles.toastMessage}`}>{message}</div>
      </div>
      <button onClick={onClose} className={styles.toastCloseButton}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
};

export const InfoToast: React.FC<{ title?: string; message: string; onClose: () => void }> = (props) => (
  <BaseToast {...props} icon={faCircleInfo} iconColor="var(--color-status-blue)" backgroundColor="var(--color-highlight)" borderColor="var(--color-status-blue)" />
);

export const SuccessToast: React.FC<{ title?: string; message: string; onClose: () => void }> = (props) => (
  <BaseToast {...props} icon={faCircleCheck} iconColor="var(--color-status-green)" backgroundColor="var(--color-status-green-light)" borderColor="var(--color-status-green)" />
);

export const WarningToast: React.FC<{ title?: string; message: string; onClose: () => void }> = (props) => (
  <BaseToast {...props} icon={faTriangleExclamation} iconColor="var(--color-status-orange)" backgroundColor="var(--color-status-orange-light)" borderColor="var(--color-status-orange)" />
);

export const ErrorToast: React.FC<{ title?: string; message: string; onClose: () => void }> = (props) => (
  <BaseToast {...props} icon={faCircleXmark} iconColor="var(--color-status-red)" backgroundColor="var(--color-status-red-light)" borderColor="var(--color-status-red)" />
);

// [2026-02-07] style-extraction: moved inline styles to SCSS module
export const CustomErrorToast: React.FC<{ content: React.ReactNode; onClose: () => void }> = ({ content, onClose }) => (
  <div className={`toast-error-custom ${styles.toastCustomError}`}>
    <div className={styles.toastIcon}>
      <FontAwesomeIcon icon={faCircleXmark} className={styles.toastCustomErrorIcon} />
    </div>
    <div className={styles.toastContent}>{content}</div>
    <button onClick={onClose} className={styles.toastCloseButton}>
      <FontAwesomeIcon icon={faXmark} />
    </button>
  </div>
);

// [2026-02-06] Removed hardcoded placeholder titles from all show* functions
export const showSuccess = (message: string, formattedMessage: string) => {
  toast.custom((t) => (
    <SuccessToast
      message={formattedMessage}
      onClose={() => toast.dismiss(t)}
    />
  ), { duration: 6000 });
};

export const showError = (message: string) => {
  toast.custom((t) => (
    <ErrorToast
      message={message}
      onClose={() => toast.dismiss(t)}
    />
  ), { duration: 6000 });
};

export const showInfo = (message: string) => {
  toast.custom((t) => (
    <InfoToast
      message={message}
      onClose={() => toast.dismiss(t)}
    />
  ), { duration: 6000 });
};

export const showWarning = (message: string) => {
  toast.custom((t) => (
    <WarningToast
      message={message}
      onClose={() => toast.dismiss(t)}
    />
  ), { duration: 6000 });
};

export const showCustomError = (content: React.ReactNode, options?: { duration?: number }) => {
  return toast.custom((t) => (
    <CustomErrorToast content={content} onClose={() => toast.dismiss(t)} />
  ), { duration: options?.duration || Infinity });
};
